'use client';

import { clsx } from 'clsx';
import { parseAsString, useQueryStates } from 'nuqs';
import { Suspense, useOptimistic, useState, useTransition } from 'react';

import { ToggleGroup } from '@/vibes/soul/form/toggle-group';
import { Streamable, useStreamable } from '@/vibes/soul/lib/streamable';
import { Accordion, AccordionItem } from '@/vibes/soul/primitives/accordion';
import { Button } from '@/vibes/soul/primitives/button';
import { CursorPaginationInfo } from '@/vibes/soul/primitives/cursor-pagination';

import { getFilterParsers } from './filter-parsers';

/** The one filter kind the listing module returns (`lib/listing.ts`): Size, In stock, Category. */
export interface ToggleGroupFilter {
  type: 'toggle-group';
  paramName: string;
  label: string;
  options: Array<{ label: string; value: string; disabled?: boolean }>;
}

interface Props {
  className?: string;
  filters: Streamable<ToggleGroupFilter[]>;
  resetFiltersLabel?: Streamable<string>;
  paginationInfo?: Streamable<CursorPaginationInfo>;
}

function getParamCountLabel(params: Record<string, string | null | string[]>, key: string) {
  const value = params[key];

  if (Array.isArray(value) && value.length > 0) return `(${value.length})`;

  return '';
}

export function FiltersPanel({ className, filters, resetFiltersLabel }: Props) {
  return (
    <Suspense fallback={<FiltersSkeleton />}>
      <FiltersPanelInner
        className={className}
        filters={filters}
        resetFiltersLabel={resetFiltersLabel}
      />
    </Suspense>
  );
}

export function FiltersPanelInner({
  className,
  filters: streamableFilters,
  resetFiltersLabel: streamableResetFiltersLabel,
  paginationInfo: streamablePaginationInfo,
}: Props) {
  const filters = useStreamable(streamableFilters);
  const resetFiltersLabel = useStreamable(streamableResetFiltersLabel) ?? 'Reset filters';
  const paginationInfo = useStreamable(streamablePaginationInfo);
  const startCursorParamName = paginationInfo?.startCursorParamName ?? 'before';
  const endCursorParamName = paginationInfo?.endCursorParamName ?? 'after';
  const [params, setParams] = useQueryStates(
    {
      ...getFilterParsers(filters),
      [startCursorParamName]: parseAsString,
      [endCursorParamName]: parseAsString,
    },
    {
      shallow: false,
      history: 'push',
    },
  );
  const [isPending, startTransition] = useTransition();
  const [optimisticParams, setOptimisticParams] = useOptimistic(params);
  // Only open/closed is state, keyed by param name: the first toggle records
  // every section then shown, and a section with no record opens if it is in
  // the first three. The sections themselves come from `filters` on every
  // render, so a page that re-renders in place (a router.refresh) shows its
  // filters as they read now.
  const [openChoices, setOpenChoices] = useState<Record<string, boolean>>({});
  const isOpen = (filter: ToggleGroupFilter, index: number) =>
    openChoices[filter.paramName] ?? index < 3;

  if (filters.length === 0) return null;

  return (
    <div className={clsx('space-y-5', className)} data-pending={isPending ? true : null}>
      <Accordion
        onValueChange={(openParamNames) =>
          setOpenChoices((prev) => ({
            ...prev,
            ...Object.fromEntries(
              filters.map((filter) => [
                filter.paramName,
                openParamNames.includes(filter.paramName),
              ]),
            ),
          }))
        }
        type="multiple"
        value={filters.filter(isOpen).map((filter) => filter.paramName)}
      >
        {filters.map((filter) => (
          <AccordionItem
            key={filter.paramName}
            title={`${filter.label}${getParamCountLabel(optimisticParams, filter.paramName)}`}
            value={filter.paramName}
          >
            <ToggleGroup
              onValueChange={(toggleGroupValues) => {
                startTransition(async () => {
                  const nextParams = {
                    ...optimisticParams,
                    [startCursorParamName]: null,
                    [endCursorParamName]: null,
                    [filter.paramName]: toggleGroupValues.length === 0 ? null : toggleGroupValues,
                  };

                  setOptimisticParams(nextParams);
                  await setParams(nextParams);
                });
              }}
              options={filter.options}
              type="multiple"
              value={optimisticParams[filter.paramName] ?? []}
            />
          </AccordionItem>
        ))}
      </Accordion>

      <Button
        onClick={() => {
          startTransition(async () => {
            const nextParams = {
              ...Object.fromEntries(Object.entries(optimisticParams).map(([key]) => [key, null])),
              [startCursorParamName]: optimisticParams[startCursorParamName],
              [endCursorParamName]: optimisticParams[endCursorParamName],
            };

            setOptimisticParams(nextParams);
            await setParams(nextParams);
          });
        }}
        size="small"
        variant="secondary"
      >
        {resetFiltersLabel}
      </Button>
    </div>
  );
}

export function FiltersSkeleton() {
  return (
    <div className="space-y-5">
      <AccordionSkeleton>
        <ToggleGroupSkeleton options={4} seed={2} />
      </AccordionSkeleton>
      <AccordionSkeleton>
        <ToggleGroupSkeleton options={3} seed={1} />
      </AccordionSkeleton>
      <AccordionSkeleton>
        <ToggleGroupSkeleton options={1} />
      </AccordionSkeleton>
      {/* Reset Filters Button */}
      <div className="h-10 w-[10ch] animate-pulse rounded-full bg-contrast-100" />
    </div>
  );
}

function AccordionSkeleton({ children }: { children: React.ReactNode }) {
  return (
    <div>
      <div className="items-start py-3 font-mono text-sm uppercase last:flex @md:py-4">
        <div className="inline-flex h-lh items-center">
          <div className="h-2 w-[10ch] flex-1 animate-pulse rounded-sm bg-contrast-100" />
        </div>
      </div>
      <div className="pb-5">{children}</div>
    </div>
  );
}

function ToggleGroupSkeleton({ options, seed = 0 }: { options: number; seed?: number }) {
  return (
    <div className="flex flex-wrap gap-2">
      {Array.from({ length: options }, (_, i) => {
        const width = Math.floor(((i * 3 + 7 + seed) % 8) + 6);

        return (
          <div
            className="h-12 w-(--width) animate-pulse rounded-full bg-contrast-100 px-4"
            key={i}
            style={{ '--width': `${width}ch` } as React.CSSProperties}
          />
        );
      })}
    </div>
  );
}
